import { useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, RadioGroup } from '@components';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import { LeadOrgConfig } from './types';

import {
  useGetLeadOrgConfigQuery,
  useUpdateLeadOrgConfigMutation
} from '../../queries/LeadManagementQuery';

const CONFIG_FIELDS: (keyof LeadOrgConfig)[] = [
  'is_lead_assignment_allowed_for_agent',
  'is_lead_deletion_allowed_for_sales_agent',
  'is_auto_assignment_enabled',
  'is_self_assignment_enabled'
];

const PERMISSION_CHECKBOXES: {
  field: keyof LeadOrgConfig;
  label: string;
  testId: string;
  // Not returned by older backends; shown only when the config includes it.
  isOptional?: boolean;
}[] = [
  {
    field: 'is_lead_assignment_allowed_for_agent',
    label: 'Re-assign leads',
    testId: 'agent-reassignment'
  },
  {
    field: 'is_lead_deletion_allowed_for_sales_agent',
    label: 'Delete leads',
    testId: 'agent-delete'
  },
  {
    field: 'is_self_assignment_enabled',
    label: 'View and self-assign unassigned leads',
    testId: 'agent-self-assign',
    isOptional: true
  }
];

const ASSIGNMENT = { MANUAL: 'manual', LOAD_BASED: 'load-based' } as const;

const ASSIGNMENT_OPTIONS = [
  { id: 'assignment-manual', key: ASSIGNMENT.MANUAL, value: 'Manual' },
  {
    id: 'assignment-load-based',
    key: ASSIGNMENT.LOAD_BASED,
    value: 'Load/capacity based'
  }
];

const toConfig = (config?: Partial<LeadOrgConfig>): LeadOrgConfig => ({
  is_lead_assignment_allowed_for_agent:
    config?.is_lead_assignment_allowed_for_agent ?? false,
  is_lead_deletion_allowed_for_sales_agent:
    config?.is_lead_deletion_allowed_for_sales_agent ?? false,
  is_auto_assignment_enabled: config?.is_auto_assignment_enabled ?? false,
  is_self_assignment_enabled: config?.is_self_assignment_enabled ?? false
});

const AgentPermissionsConfig = () => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();

  const hasConfigView = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW
  ]);
  const hasConfigEdit = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_EDIT
  ]);

  const {
    data: orgConfig,
    isError,
    refetch
  } = useGetLeadOrgConfigQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !hasConfigView }
  );

  const [updateOrgConfig, { isLoading: isSaving }] =
    useUpdateLeadOrgConfigMutation();

  const savedConfig = useMemo(() => toConfig(orgConfig), [orgConfig]);
  const [config, setConfig] = useState<LeadOrgConfig>(savedConfig);

  useEffect(() => {
    setConfig(savedConfig);
  }, [savedConfig]);

  const isDirty = CONFIG_FIELDS.some(
    (field) => config[field] !== savedConfig[field]
  );
  // Until the config loads, defaults are placeholders; saving them would
  // overwrite permissions the user never touched.
  const isDisabled = !hasConfigEdit || isSaving || !orgConfig;
  // Older backends do not return newer fields, so those cannot be saved.
  const isReturned = (field: keyof LeadOrgConfig) =>
    typeof orgConfig?.[field] === 'boolean';
  const supportsAutoAssignment = isReturned('is_auto_assignment_enabled');

  const handleSave = async () => {
    try {
      await updateOrgConfig({
        organizationId: organizationId || '',
        body: config
      }).unwrap();
      createSuccessAlert('Configuration updated successfully');
    } catch {
      createErrorAlert('Failed to update configuration. Please try again.');
    }
  };

  if (!hasConfigView) return null;

  if (isError) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-xl bg-white p-6">
        <p className="text-base text-cofeeNeutral10">
          Could not load agent permissions.
        </p>
        <Button
          variant="tertiary"
          size="small"
          label="Retry"
          className="w-fit px-4"
          handleClick={refetch}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 rounded-xl bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="text-lg font-semibold text-cofeeNeutral10">
          Permissions for agents
        </div>
        {PERMISSION_CHECKBOXES.filter(
          ({ field, isOptional }) => !isOptional || isReturned(field)
        ).map(({ field, label, testId }) => (
          <Checkbox
            key={field}
            htmlKey={testId}
            data-testid={testId}
            label={label}
            isChecked={config[field]}
            handleChange={(e) =>
              setConfig((prev) => ({ ...prev, [field]: e.target.checked }))
            }
            disabled={isDisabled}
            variant="v2"
            labelStyle="!text-base !font-medium !text-cofeeNeutral13"
          />
        ))}
      </div>
      {supportsAutoAssignment && (
        <div className="flex flex-col gap-4 border-t border-cofeeGreyBlue4 pt-6">
          <div className="text-lg font-semibold text-cofeeNeutral10">
            Automatic assignment
          </div>
          <RadioGroup
            options={ASSIGNMENT_OPTIONS}
            selectedValue={
              config.is_auto_assignment_enabled
                ? ASSIGNMENT.LOAD_BASED
                : ASSIGNMENT.MANUAL
            }
            handleChange={(value) =>
              setConfig((prev) => ({
                ...prev,
                is_auto_assignment_enabled: value === ASSIGNMENT.LOAD_BASED
              }))
            }
            disabled={isDisabled}
            orientation="vertical"
            labelStyle="!ml-2 !text-base !font-medium !text-cofeeNeutral13"
            optionClassName="!mb-0"
            className="gap-4"
          />
        </div>
      )}
      <Button
        variant="primary"
        size="small"
        label="Save"
        className="w-fit px-4"
        handleClick={handleSave}
        disabled={isDisabled || !isDirty}
        loading={isSaving}
      />
    </div>
  );
};

export default AgentPermissionsConfig;
